<template>
  <div>
    <template v-if="show && !connectBankAccountVisible && connectBankAccountSuccessful">
      <div class="d-flex justify-content-center">
            <div
            id="toastSimple"
            class="toast d-flex align-items-center justify-content-between hide"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div class="toast-body">Bank account connected!</div>
            <button
              type="button"
              class="btn-close me-3"
              data-bs-dismiss="toast"
              aria-label="Close"
              @click="closeToast()"
            ></button>
          </div>
        </div>
    </template>
    <client-only v-if="!smsOptInModal">
      <PortalModal
        v-if="canShowAppCompleteModal && appCompleteModal"
        :modalContent="appCompleteModal"
      ></PortalModal>
      <PortalModal
        v-else-if="canShowRenewalAppCompleteModal && renewalAppCompleteModal"
        :modalContent="renewalAppCompleteModal"
      ></PortalModal>
    </client-only>
    <client-only v-if="!appCompleteModal">
      <PortalSmsOptInModal
        v-if="smsOptInModal"
        :modalContent="smsOptInModal"
      ></PortalSmsOptInModal>
    </client-only>
    <div
      v-if="isQualifiedBorrower"
      class="d-flex flex-column"
      id="borrower-qualified"
    >
      <QualifiedPortalDashboard></QualifiedPortalDashboard>
    </div>
    <!-- ***** UNQUALIFIED/DEFAULT ****** -->
    <div v-else class="d-flex flex-column" id="borrower-dnq">
      <div v-if="showDNQCta">
        <DnqCta />
      </div>
      <div v-else>
        <transition name="component-fade" mode="out-in">
          <component
            :is="defaultDisplayComponent"
            @updateCriteria="updateCriteria"
            @updateShowAppCompleteModal="updateShowAppCompleteModal"
          />
        </transition>
      </div>
    </div>
    <template v-if="connectBankAccountVisible || connectBankAccountAttempted">
      <BankConnectModal @conAccountDone="removeBankConnectParameter()" />
    </template>
    <ChatBot />
  </div>
    <PortalSbaInviteModal v-if="isSbaQualified && !hasVisitedSBA && canShowAppCompleteModal && appCompleteModal" :showModal="showModal" @close="showModal = false" />
</template>

<script>
import { mapState, mapActions } from 'pinia'
import get from 'lodash/get'
import SmoothScroll from '~/libs/smooth-scroll'
import QualifiedPortalDashboard from '~/components/portal/portalViews/QualifiedPortalDashboard.vue'
import ChatBot from '~/components/ChatBot'
import BankConnectModal from '~/components/BankConnectModal'
import DefaultPortalDashboard from '~/components/portal/portalViews/DefaultPortalDashboard.vue'
import FinancingCriteria from '~/components/portal/portalViews/FinancingCriteria.vue'
import PortalModal from '~/components/portal/PortalModal.vue'
import PortalSmsOptInModal from '~/components/portal/SmsOptInModal'
import DnqCta from '~/pages/portal/DnqCta.vue'
import { mineralTypes } from '~/libs/mineral-calculation'
import { usePortalStore } from '~/store/portal'
import { useBorrowerStore } from '~/store/borrower'
import { useSunriseFinicityStore } from '~/store/sunrise-finicity'
import { localStorageService } from '~/libs/local-storage.service'
import {useProgressStore} from "../../store/progress.js";
import { useProgressCardStore } from '~/store/progress-card'
import { useExperimentsStore } from '~/store/experiments'
import PortalSbaInviteModal from '~/components/portal/PortalSbaInviteModal.vue'

export default {
  name: 'Portal',
  data() {
    return {
      show: false,
      showCriteria: false,
      showModal: true,
      mineralTypes: mineralTypes,
      errorHeader: 'Could not connect to bank',
      errorMessage: 'We are having issues connecting with our bank connection service. Please try to upload your bank statements manually or try again later.',
      showDNQAppCompleteModal: null,
    }
  },
  watch: {
    connectBankAccountVisible (newVal) {
      if (newVal === false){
        this.showToast()
      }
    }
  },
  computed: {
    ...mapState(useProgressCardStore, [
      'isSbaQualified', 'hasVisitedSBA'
    ]),
    ...mapState(usePortalStore, [
      'showDNQCta',
      'isQualifiedBorrower',
      'getModalContent'
    ]),
    ...mapState(useBorrowerStore, {
      mineralGroup: (state) => get(state, 'borrower.mineralGroup'),
      smsOptIn: (state) =>
        get(state, 'borrowerValues.marketingSmsOptIn')
    }),
    ...mapState(useSunriseFinicityStore, {
      connectBankAccountVisible: store => get(store, 'connectBankAccountVisible'),
      finicityConnectLoading: store => get(store, 'finicityConnectLoading'),
      sunriseApiError: store => get(store, 'sunriseApiError'),
      connectBankAccountSuccessful: store => get(store, 'connectBankAccountSuccessful'),
    }),
    ...mapState(useProgressStore, {
      isRenewalAppComplete: store => get(store, 'isRenewalAppComplete'),
    }),
    appCompleteModal() {
      return this.getModalContent('appComplete')
    },
    canShowAppCompleteModal() {
      return this.isQualifiedBorrower || Boolean(this.showDNQAppCompleteModal)
    },
    renewalAppCompleteModal() {
      return this.getModalContent('renewalAppComplete')
    },
    canShowRenewalAppCompleteModal() {
      return this.isRenewalAppComplete
    },
    smsOptInModal() {
      return this.getModalContent('smsOptIn')
    },
    defaultDisplayComponent() {
      return 'DefaultPortalDashboard'
    },
    connectBankAccountAttempted() {
      return this.connectBankAccountSuccessful === 'true' || this.connectBankAccountSuccessful === 'false'
    },
  },
  components: {
    QualifiedPortalDashboard,
    DefaultPortalDashboard,
    FinancingCriteria,
    PortalModal,
    PortalSmsOptInModal,
    DnqCta,
    BankConnectModal,
    ChatBot,
    PortalSbaInviteModal
},
  async mounted() {
    const finicityStore = useSunriseFinicityStore(this.$pinia)
    SmoothScroll.goTo(0)

    const appCompleted = this.$route.query.completedApp === 'true';
    if (appCompleted && window.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler('closeHandler')
    }

    finicityStore.$patch({ connectBankAccountSuccessful: null })
    const bankConnectModalVisibility = this.$route.query.showBankConnectModal
    if (bankConnectModalVisibility && !this.connectBankAccountAttempted){
      finicityStore.$patch({ connectBankAccountVisible: true })
    }

    if (this.showDNQAppCompleteModal === false) {
      this.removeAppCompleteParam()
    }

    useExperimentsStore(this.$pinia).fetchActiveExperimentsForCurrentUser()

    this.fastTrackTest()
  },
  methods: {
    ...mapActions(useBorrowerStore, ['fastTrackTest']),
    updateCriteria(response) {
      this.showCriteria = response
    },
    removeBankConnectParameter() {
      this.$router
        .replace({ 'query.showBankConnectModal': null })
        .catch((error) => { })
    },
    removeAppCompleteParam() {
      if (process.client && localStorageService.getItem('portalModal') === 'appComplete') {
        localStorageService.removeItem('portalModal')
      }
    },
    updateShowAppCompleteModal(show) {
      this.showDNQAppCompleteModal = show
      if (!show) {
        this.removeAppCompleteParam()
      }
    },
    showToast() {
      setTimeout(() => {
        this.show = true
      }, 100)
       setTimeout(() => {
        this.closeToast()
      }, 5000)
    },
    closeToast() {
      this.show = false
    },
  }
}
</script>

<style scoped lang="scss">
@import 'assets/styles/global';
@import "lendio-toasts";
.pageDetail {
  margin-top: 30px;
}

.criteria-text {
  font-size: 20px;
  margin-bottom: 48px;
}
.component-fade-enter-active,
.component-fade-leave-active {
  transition: opacity 0.2s ease;
}
.component-fade-enter,
.component-fade-leave-to {
  opacity: 0;
}

@include media-breakpoint-down(md) {
  .widget-container {
    display: flex;
    justify-content: center;
  }
}

.container.welcome,
.portal-scorecards {
  margin-bottom: 1%;
}
</style>
